import env from "@/env.mjs";
import type {
  FieldOrFunction,
  ICentraFilterUri,
  IFilterGroup,
  ISortOrderValue,
} from "@/lib/types/centra";

import type { CentraProduct } from "@/lib/centra/atomicSetup";
import type { ProductCard } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import type { IFormattedFilters } from "./types";
import type { RawFilterResponse } from "./useProductFilter";

export type GetProductsReturn = Awaited<ReturnType<typeof getProducts>>;

type IFilterBodyTypes =
  | boolean
  | number
  | string
  | undefined
  | string[]
  | ISortOrderValue[]
  | ICentraFilterUri;

interface IFilterBody {
  uri: ICentraFilterUri | undefined;
  relatedProducts: boolean;
  limit: number;
  skipFirst: number;
  search: string | undefined;
  sortOrder: ISortOrderValue[];
  [index: string]: IFilterBodyTypes;
}

export type GetProducts = <PC extends ProductCard, ExtraData>({
  cardFormatter,
  category,
  filters,
  productIds,
  search,
  sortOrder,
  page,
  productsPerPage,
  extraDataFetcher,
  customerToken,
  apiSecret,
}: // new
{
  cardFormatter: (product: CentraProduct, data?: ExtraData) => PC;
  category: string[] | [];
  filters?: IFormattedFilters;
  filterGroups?: IFilterGroup[];
  productIds?: string[] | undefined;
  search?: string;
  sortOrder?: ISortOrderValue[];
  page?: number;
  productsPerPage?: number;
  customerToken?: string;
  apiSecret?: string;
  extraDataFetcher?: (productIds: string[]) => Promise<ExtraData>;
  signal?: AbortSignal;
}) => Promise<{
  products: PC[];
  filter: any[];
  productCount: number;
}>;

export type GetProductsParameters = Parameters<typeof getProducts>[0];

const formatFilterGroups = (
  filterGroups: IFilterGroup[],
): IFormattedFilters => {
  const group: IFormattedFilters = filterGroups.map(group => ({
    name: group.name,
    fieldOrFunction: group.fieldOrFunction as FieldOrFunction,
    required: group.required ?? false,
    labelFieldOrFunction: group.labelFieldOrFunction,
    values: undefined,
    toggle: (_value: string) => {},
    add: (_value: string) => {},
    remove: (_value: string) => {},
    clear: () => {},
    isOpen: () => false,
    toggleOpen: () => {},
    isActive: (_value: string) => false,
  }));
  return group;
};

export const getProducts: GetProducts = async ({
  cardFormatter,
  category,
  filters = [],
  filterGroups,
  productIds,
  search = "",
  sortOrder = [],
  page = 0,
  productsPerPage = 8,
  extraDataFetcher,
  customerToken,
  apiSecret,
  signal,
}) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  if (filters.length === 0 || filters === undefined) {
    if (filterGroups) filters = formatFilterGroups(filterGroups);
  }

  const categoryUri =
    category?.length > 0
      ? search
        ? undefined
        : { uri: category.join("/"), for: ["category"] }
      : undefined;

  const filterBody: IFilterBody = {
    uri: categoryUri,
    products: productIds,
    relatedProducts: true,
    limit: productsPerPage,
    skipFirst: page * productsPerPage,
    search: search,
    sortOrder: sortOrder,
    pricelist: apiSecret ? "all" : undefined,
  };

  if (filters) {
    const activeFilters = filters.filter(
      filter => filter.values?.length && filter.values.length > 0,
    );
    if (activeFilters.length > 0) {
      filterBody["onlyAvailable"] = true;
    }
    filters.forEach(filter =>
      typeof filter.fieldOrFunction === "string"
        ? (filterBody[filter.fieldOrFunction] = filter.values)
        : filter.fieldOrFunction !== undefined &&
          (filterBody[filter.fieldOrFunction([]).field] = filter.values),
    );
  }

  try {
    // RawFilterResponse;
    const authHeader: Record<string, string> = apiSecret
      ? { Authorization: `Bearer ${apiSecret}` }
      : { "API-token": customerToken || "" };

    const res = await fetch(`${apiUrl}/products`, {
      headers: {
        ...authHeader,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(filterBody),
      cache: "no-store",
      signal,
    });

    if (!res.ok) throw new Error("Error fetching products");
    const resData: RawFilterResponse = await res.json();
    let products = resData.products;
    const extraData = extraDataFetcher
      ? await extraDataFetcher(products.map(product => product.product))
      : undefined;

    /* Remove engraving from PLP's */
    products = products.filter(product => product.uri !== "engraving");

    const formattedProducts = products.map(product => {
      return cardFormatter(product, extraData);
    });
    const filterFields = filters.map(filter => filter.fieldOrFunction);
    const formattedFilters =
      filters.length > 0
        ? resData.filter.filter(
            centraFilter =>
              filterFields.findIndex((field: any) =>
                typeof field === "string"
                  ? field === centraFilter.field
                  : field(resData.filter).field === centraFilter.field,
              ) !== -1,
          )
        : [];
    // Allowlist the fields callers actually use rather than spreading `resData`.
    // This result crosses the server->client boundary in `getInitialPlpData` and
    // `features/search/actions.ts`, and Centra's raw response also carries a
    // selection `token` plus a `debugData` block (query timings, dyn_url) — a
    // spread serialises those into the RSC payload for every visitor to read.
    return {
      products: formattedProducts,
      filter: formattedFilters,
      productCount: resData.productCount,
    };
  } catch (error) {
    throw error;
  }
};
